// contexts/LanguageContext.js - ReactJS Web Version
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import translationService from '../utils/translationService';
import { useReset } from './ResetContext';
import { useReload } from './ReloadContext';

export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸', direction: 'ltr' },
  { code: 'es', name: 'Español', flag: '🇪🇸', direction: 'ltr' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', direction: 'ltr' },
  { code: 'ht', name: 'Kreyòl Ayisyen', flag: '🇭🇹', direction: 'ltr' }
];

// Country to language mapping
const countryLanguageMap = {
  'HT': 'ht', // Haiti - Haitian Creole
  'FR': 'fr', // France
  'CA': 'fr', // Canada (French)
  'BE': 'fr', // Belgium (French)
  'CH': 'fr', // Switzerland (French)
  'ES': 'es', // Spain
  'MX': 'es', // Mexico
  'US': 'en', // United States
  'GB': 'en', // United Kingdom
  'AU': 'en', // Australia
  'NZ': 'en', // New Zealand
};

// Default to English if country not found
const DEFAULT_LANGUAGE = 'en';
const DEFAULT_COUNTRY = 'US';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  const [translations, setTranslations] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationQueue, setTranslationQueue] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [languageChangeCount, setLanguageChangeCount] = useState(0);
  const [locationDetected, setLocationDetected] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationMethod, setLocationMethod] = useState(null); // 'gps', 'ip', 'default'
  const [locationPermission, setLocationPermission] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  
  const { registerReset, unregisterReset } = useReset();
  const { softReload } = useReload();

  // ======================================================
  // DETECT LOCATION USING BROWSER GEOLOCATION API
  // ======================================================
  const detectLocationByGPS = useCallback(() => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.log('❌ Geolocation not supported by this browser');
        resolve(null);
        return;
      }

      console.log('📍 Attempting GPS location detection...');
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          console.log('📍 GPS Location obtained:', { latitude, longitude });
          
          try {
            // Reverse geocode using free API
            const response = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            const data = await response.json();
            
            if (data && data.countryCode) {
              console.log('📍 Country detected via GPS:', data.countryCode);
              resolve({
                latitude,
                longitude,
                country: data.countryName,
                countryCode: data.countryCode,
                city: data.city || data.locality || 'Unknown',
                region: data.principalSubdivision || 'Unknown',
              });
            } else {
              resolve(null);
            }
          } catch (error) {
            console.error('❌ Reverse geocoding error:', error);
            resolve(null);
          }
        },
        (error) => {
          console.warn('❌ GPS location error:', error.message);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 60000,
        }
      );
    });
  }, []);

  // ======================================================
  // DETECT LOCATION USING IP ADDRESS (Fallback)
  // ======================================================
  const detectLocationByIP = useCallback(async () => {
    try {
      console.log('📍 Attempting IP location detection...');
      
      // Try multiple IP geolocation services
      const services = [
        'https://ipapi.co/json/',
        'https://ip-api.com/json/',
        'https://api.ipify.org?format=json',
      ];

      for (const service of services) {
        try {
          const response = await fetch(service);
          const data = await response.json();
          
          if (data && (data.country_code || data.countryCode || data.country)) {
            const countryCode = (data.country_code || data.countryCode || data.country).toUpperCase();
            const countryName = data.country_name || data.country || data.countryName;
            
            console.log('📍 IP Location detected:', { countryCode, countryName });
            
            return {
              latitude: data.latitude || data.lat || 0,
              longitude: data.longitude || data.lon || 0,
              country: countryName,
              countryCode: countryCode,
              city: data.city || 'Unknown',
              region: data.region || 'Unknown',
            };
          }
        } catch (e) {
          console.warn(`⚠️ IP service ${service} failed:`, e.message);
          continue;
        }
      }
      
      return null;
    } catch (error) {
      console.error('❌ IP location detection error:', error);
      return null;
    }
  }, []);

  // ======================================================
  // DETECT USER'S COUNTRY (Main function)
  // ======================================================
  const detectUserLocation = useCallback(async () => {
    if (isDetectingLocation) {
      console.log('⏳ Location detection already in progress');
      return null;
    }

    try {
      setIsDetectingLocation(true);
      console.log('📍 Starting location detection...');
      
      // Try GPS first
      let location = await detectLocationByGPS();
      
      // If GPS fails, try IP
      if (!location) {
        console.log('📍 GPS failed, trying IP detection...');
        location = await detectLocationByIP();
        setLocationPermission('denied');
        setLocationMethod('ip');
      } else {
        setLocationPermission('granted');
        setLocationMethod('gps');
      }

      if (location && location.countryCode) {
        const countryCode = location.countryCode.toUpperCase();
        console.log('📍 Detected country code:', countryCode);
        setDetectedCountry(countryCode);
        setUserLocation(location);
        
        // Check if country is in our mapping
        if (countryCode && countryLanguageMap[countryCode]) {
          const detectedLanguage = countryLanguageMap[countryCode];
          console.log(`📍 User in ${countryCode}, detected language: ${detectedLanguage}`);
          setIsDetectingLocation(false);
          return detectedLanguage;
        } else {
          console.log(`📍 Country ${countryCode} not in mapping, using default language`);
          setIsDetectingLocation(false);
          return DEFAULT_LANGUAGE;
        }
      }
      
      // If all methods fail, use default
      console.log('📍 Using default location (US/English)');
      setDetectedCountry(DEFAULT_COUNTRY);
      setLocationMethod('default');
      setIsDetectingLocation(false);
      return DEFAULT_LANGUAGE;
      
    } catch (error) {
      console.error('❌ Location detection error:', error);
      setIsDetectingLocation(false);
      return null;
    }
  }, [isDetectingLocation, detectLocationByGPS, detectLocationByIP]);

  // ======================================================
  // RESET LANGUAGE STATE
  // ======================================================
  const resetLanguageState = useCallback(() => {
    console.log('🔄 Resetting Language state...');
    setTranslations({});
    setIsTranslating(false);
    setTranslationQueue([]);
  }, []);

  // Register reset function with ResetContext
  useEffect(() => {
    registerReset('language', resetLanguageState);
    
    return () => {
      unregisterReset('language');
    };
  }, [registerReset, unregisterReset, resetLanguageState]);

  // Load saved language and detect location on mount
  useEffect(() => {
    initializeLanguage();
  }, []);

  const initializeLanguage = async () => {
    try {
      console.log('🔄 Initializing language...');
      
      // First, check if language is already saved in localStorage
      const savedLanguage = localStorage.getItem('app_language');
      
      // If language is saved, use it
      if (savedLanguage && languages.find(l => l.code === savedLanguage)) {
        console.log('✅ Using saved language:', savedLanguage);
        setCurrentLanguage(savedLanguage);
        setIsInitialized(true);
        return;
      }
      
      // If no saved language, detect from location
      console.log('🔍 No saved language found, detecting from location...');
      const detectedLang = await detectUserLocation();
      
      if (detectedLang) {
        console.log(`✅ Setting language to detected: ${detectedLang}`);
        setCurrentLanguage(detectedLang);
        setLocationDetected(true);
        // Save the detected language
        localStorage.setItem('app_language', detectedLang);
      } else {
        console.log(`✅ Using default language: ${DEFAULT_LANGUAGE}`);
        setCurrentLanguage(DEFAULT_LANGUAGE);
        // Don't save default language to storage
      }
      
      setLocationDetected(true);
    } catch (error) {
      console.error('Error initializing language:', error);
      setCurrentLanguage(DEFAULT_LANGUAGE);
    } finally {
      setIsInitialized(true);
    }
  };

  // Save language when it changes (and not default)
  useEffect(() => {
    if (isInitialized && currentLanguage !== DEFAULT_LANGUAGE) {
      saveLanguage(currentLanguage);
    } else if (isInitialized && currentLanguage === DEFAULT_LANGUAGE) {
      // Remove saved language if default
      localStorage.removeItem('app_language');
    }
  }, [currentLanguage, isInitialized]);

  const saveLanguage = (languageCode) => {
    try {
      localStorage.setItem('app_language', languageCode);
      console.log('💾 Language saved:', languageCode);
    } catch (error) {
      console.error('Error saving language:', error);
    }
  };

  // Change language function
  const changeLanguage = async (languageCode) => {
    if (languageCode === currentLanguage) {
      console.log('⚠️ Language already set to:', languageCode);
      return;
    }
    
    console.log('🌐 Changing language from', currentLanguage, 'to', languageCode);
    
    setIsTranslating(true);
    
    // Update language
    setCurrentLanguage(languageCode);
    
    // Increment change counter to force re-renders
    setLanguageChangeCount(prev => prev + 1);
    
    // Clear translation cache ONLY - NOT auth or profile data
    if (translationService && translationService.clearCache) {
      translationService.clearCache();
    }
    
    // Clear translation state ONLY
    setTranslations({});
    setTranslationQueue([]);
    
    setIsTranslating(false);
    
    // ✅ TRIGGER SOFT RELOAD TO RESET ALL SCREENS
    console.log('🔄 Triggering soft reload to refresh all screens with new language');
    
    // Use setTimeout to ensure state updates complete before reload
    setTimeout(() => {
      if (softReload) {
        softReload();
        console.log('✅ Soft reload triggered successfully');
      } else {
        // Fallback: reload the page
        window.location.reload();
      }
    }, 200);
  };

  // Force detect location (exposed for manual use)
  const forceDetectLocation = useCallback(async () => {
    console.log('🔄 Forcing location detection...');
    const detectedLang = await detectUserLocation();
    if (detectedLang && detectedLang !== currentLanguage) {
      await changeLanguage(detectedLang);
      return detectedLang;
    }
    return currentLanguage;
  }, [detectUserLocation, currentLanguage, changeLanguage]);

  const translateText = async (text, options = {}) => {
    if (!text || typeof text !== 'string') return text;
    if (currentLanguage === 'en') return text;
    
    const { adaptive = false, context = '' } = options;
    
    try {
      if (translationService) {
        if (adaptive && translationService.adaptiveTranslate) {
          return await translationService.adaptiveTranslate(text, currentLanguage, context);
        }
        if (translationService.translateText) {
          return await translationService.translateText(text, currentLanguage);
        }
      }
      // Fallback: return original text if translation service not available
      return text;
    } catch (error) {
      console.error('Translation failed:', error);
      return text;
    }
  };

  const translateBatch = async (texts, options = {}) => {
    if (!texts || texts.length === 0 || currentLanguage === 'en') return texts;
    
    try {
      if (translationService && translationService.batchTranslateTexts) {
        return await translationService.batchTranslateTexts(texts, currentLanguage);
      }
      return texts;
    } catch (error) {
      console.error('Batch translation failed:', error);
      return texts;
    }
  };

  const translateMessage = async (message, options = {}) => {
    if (!message || currentLanguage === 'en') return message;
    
    const { preserveEmojis = true } = options;
    
    try {
      if (translationService && translationService.translateMessage) {
        return await translationService.translateMessage(message, currentLanguage, preserveEmojis);
      }
      return message;
    } catch (error) {
      console.error('Message translation failed:', error);
      return message;
    }
  };

  const smartTranslate = async (text, originalLanguage = 'en') => {
    if (!text || currentLanguage === originalLanguage) return text;
    
    try {
      if (!originalLanguage || originalLanguage === 'en') {
        if (translationService && translationService.detectLanguage) {
          const detected = await translationService.detectLanguage(text);
          if (detected === currentLanguage) return text;
        }
      }
      
      if (translationService && translationService.translateText) {
        return await translationService.translateText(text, currentLanguage);
      }
      return text;
    } catch (error) {
      console.error('Smart translation failed:', error);
      return text;
    }
  };

  const clearTranslationCache = useCallback(() => {
    if (translationService && translationService.clearCache) {
      translationService.clearCache();
    }
    setTranslations({});
    setTranslationQueue([]);
  }, []);

  const getSupportedLanguages = useCallback(() => {
    return languages;
  }, []);

  const getLanguageByCode = useCallback((code) => {
    return languages.find(l => l.code === code) || languages[0];
  }, []);

  const isRTL = useCallback(() => {
    const lang = getLanguageByCode(currentLanguage);
    return lang?.direction === 'rtl';
  }, [currentLanguage, getLanguageByCode]);

  const isLanguageSupported = useCallback((code) => {
    return languages.some(l => l.code === code);
  }, []);

  const value = {
    currentLanguage,
    changeLanguage,
    languages,
    translateText,
    translateBatch,
    translateMessage,
    smartTranslate,
    isTranslating,
    t: translateText,
    isInitialized,
    clearTranslationCache,
    getSupportedLanguages,
    getLanguageByCode,
    isRTL,
    isLanguageSupported,
    translations,
    languageChangeCount,
    locationDetected,
    detectedCountry,
    isDetectingLocation,
    locationMethod,
    locationPermission,
    userLocation,
    detectUserLocation: forceDetectLocation, // Expose for manual detection
  };

  if (!isInitialized) {
    // Show loading screen while initializing
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Detecting your location...</p>
        </div>
      </div>
    );
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;